/**
 * Tier Framework Utility
 *
 * Provides student-contextualized tier definitions, portfolio validation,
 * and smart messaging for college admissions analysis.
 */

// ============================================================================
// TIER DEFINITIONS
// ============================================================================

export const STUDENT_TIERS = {
  TIER_1: {
    id: 1,
    label: 'Outstanding',
    description: '99th percentile - Top 1% of test takers',
    criteria: {
      sat_min: 1450,
      act_min: 33,
      gpa_min: 3.9
    },
    definitions: {
      reach: {
        acceptance_max: 0.15,
        description: '<15% acceptance (Ivy+, Stanford, MIT, Caltech, Duke)',
        note: 'These are reaches for everyone regardless of scores'
      },
      target: {
        acceptance_min: 0.15,
        acceptance_max: 0.35,
        minimum_above_25: 2, // At least 2 schools with ≥25% acceptance
        description: '15-35% acceptance',
        breakdown: 'Mix of 15-25% (competitive) and 25-35% (strong match)',
        examples: 'Northeastern, BU, Tulane, Rochester, Emory (if >15%)'
      },
      safety: {
        acceptance_min: 0.35,
        minimum_above_50: 1, // At least 1 school with >50% acceptance
        description: '>35% acceptance',
        examples: 'Penn State, UW-Madison, UMD, Ohio State'
      }
    }
  },

  TIER_2: {
    id: 2,
    label: 'Excellent',
    description: '92-98th percentile',
    criteria: {
      sat_min: 1350,
      sat_max: 1449,
      act_min: 28,
      act_max: 32,
      gpa_min: 3.6,
      gpa_max: 3.89
    },
    definitions: {
      reach: {
        acceptance_max: 0.20,
        description: '<20% acceptance (Many T30 schools)',
        note: 'You\'re qualified but admission is highly competitive'
      },
      target: {
        acceptance_min: 0.20,
        acceptance_max: 0.40,
        minimum_above_30: 2, // At least 2 schools with ≥30% acceptance
        description: '20-40% acceptance',
        examples: 'Lehigh, Brandeis, Case Western, Pepperdine'
      },
      safety: {
        acceptance_min: 0.40,
        minimum_above_55: 1, // At least 1 school with >55% acceptance
        description: '>40% acceptance',
        examples: 'UMass Amherst, Rutgers, UConn, Indiana'
      }
    }
  },

  TIER_3: {
    id: 3,
    label: 'Strong',
    description: '75-89th percentile',
    criteria: {
      sat_min: 1150,
      sat_max: 1349,
      act_min: 24,
      act_max: 27,
      gpa_min: 3.2,
      gpa_max: 3.59
    },
    definitions: {
      reach: {
        acceptance_max: 0.25,
        description: '<25% acceptance (Strong state flagships and selective privates)',
        note: 'Competitive but within possibility'
      },
      target: {
        acceptance_min: 0.25,
        acceptance_max: 0.50,
        minimum_above_35: 2, // At least 2 schools with ≥35% acceptance
        description: '25-50% acceptance',
        note: 'Sweet spot for admission likelihood'
      },
      safety: {
        acceptance_min: 0.50,
        minimum_above_60: 1, // At least 1 school with >60% acceptance
        description: '>50% acceptance',
        note: 'Very likely admission'
      }
    }
  },

  TIER_4: {
    id: 4,
    label: 'Above Average',
    description: '50-74th percentile',
    criteria: {
      sat_min: 1010,
      sat_max: 1149,
      act_min: 20,
      act_max: 23,
      gpa_min: 2.8,
      gpa_max: 3.19
    },
    definitions: {
      reach: {
        acceptance_max: 0.35,
        description: '<35% acceptance (Reach for solid state schools)',
        note: 'Worth trying but not entire list'
      },
      target: {
        acceptance_min: 0.35,
        acceptance_max: 0.60,
        minimum_above_45: 2, // At least 2 schools with ≥45% acceptance
        description: '35-60% acceptance',
        note: 'Best statistical match'
      },
      safety: {
        acceptance_min: 0.60,
        minimum_above_70: 1, // At least 1 school with >70% acceptance
        description: '>60% acceptance',
        note: 'Strong likelihood of admission'
      }
    }
  },

  TIER_5: {
    id: 5,
    label: 'Average',
    description: '<50th percentile',
    criteria: {
      sat_max: 1009,
      act_max: 19,
      gpa_max: 2.79
    },
    definitions: {
      reach: {
        acceptance_max: 0.45,
        description: '<45% acceptance',
        note: 'Aspirational but keep limited'
      },
      target: {
        acceptance_min: 0.45,
        acceptance_max: 0.70,
        description: '45-70% acceptance',
        note: 'Your best admission probability'
      },
      safety: {
        acceptance_min: 0.70,
        description: '>70% acceptance',
        note: 'Very likely admission, many good options here'
      }
    }
  }
};

// ============================================================================
// CORE FUNCTIONS
// ============================================================================

/**
 * Determine student tier based on test scores and GPA
 */
export function getStudentTier(studentProfile) {
  if (!studentProfile) return STUDENT_TIERS.TIER_3; // Default to middle tier

  // Extract values with comprehensive field name coverage
  // Try multiple field names to ensure we catch the data regardless of schema
  const sat = studentProfile.sat_total ||
              studentProfile.sat_score ||
              studentProfile.test_scores?.sat ||
              studentProfile.test_scores?.sat_total ||
              null;

  const act = studentProfile.act_composite ||
              studentProfile.act_score ||
              studentProfile.test_scores?.act ||
              studentProfile.test_scores?.act_composite ||
              null;

  const gpa = studentProfile.gpa_weighted ||
              studentProfile.gpa_unweighted ||
              studentProfile.gpa ||
              studentProfile.academic_performance?.gpa ||
              studentProfile.academic_performance?.gpa_weighted ||
              studentProfile.academic_performance?.gpa_unweighted ||
              null;

  // Debug logging (remove in production if needed)
  console.log('🎯 Tier calculation:', { sat, act, gpa, profile: studentProfile });

  // Calculate tier for each metric independently
  const tiers = [];

  // SAT tier
  if (sat !== null && sat > 0) {
    if (sat >= STUDENT_TIERS.TIER_1.criteria.sat_min) {
      tiers.push(1);
    } else if (sat >= STUDENT_TIERS.TIER_2.criteria.sat_min) {
      tiers.push(2);
    } else if (sat >= STUDENT_TIERS.TIER_3.criteria.sat_min) {
      tiers.push(3);
    } else if (sat >= STUDENT_TIERS.TIER_4.criteria.sat_min) {
      tiers.push(4);
    } else {
      tiers.push(5);
    }
  }

  // ACT tier
  if (act !== null && act > 0) {
    if (act >= STUDENT_TIERS.TIER_1.criteria.act_min) {
      tiers.push(1);
    } else if (act >= STUDENT_TIERS.TIER_2.criteria.act_min) {
      tiers.push(2);
    } else if (act >= STUDENT_TIERS.TIER_3.criteria.act_min) {
      tiers.push(3);
    } else if (act >= STUDENT_TIERS.TIER_4.criteria.act_min) {
      tiers.push(4);
    } else {
      tiers.push(5);
    }
  }

  // GPA tier
  if (gpa !== null && gpa > 0) {
    if (gpa >= STUDENT_TIERS.TIER_1.criteria.gpa_min) {
      tiers.push(1);
    } else if (gpa >= STUDENT_TIERS.TIER_2.criteria.gpa_min) {
      tiers.push(2);
    } else if (gpa >= STUDENT_TIERS.TIER_3.criteria.gpa_min) {
      tiers.push(3);
    } else if (gpa >= STUDENT_TIERS.TIER_4.criteria.gpa_min) {
      tiers.push(4);
    } else {
      tiers.push(5);
    }
  }

  // Use the BEST (lowest number = highest tier) tier across all metrics
  if (tiers.length === 0) {
    console.warn('⚠️ No valid metrics found for tier calculation, defaulting to TIER_3');
    return STUDENT_TIERS.TIER_3;
  }

  const bestTier = Math.min(...tiers);
  console.log('✅ Calculated tier:', bestTier, 'from metrics:', tiers);

  return STUDENT_TIERS[`TIER_${bestTier}`];
}

/**
 * Get tier-specific reach/target/safety definitions
 */
export function getTierDefinitions(studentTier) {
  if (!studentTier || !studentTier.definitions) {
    return STUDENT_TIERS.TIER_3.definitions; // Fallback to middle tier
  }

  return studentTier.definitions;
}

/**
 * Get simplified tier label for display
 */
export function getTierLabel(tier) {
  if (typeof tier === 'number') {
    return STUDENT_TIERS[`TIER_${tier}`]?.label || 'Unknown';
  }
  return tier?.label || 'Unknown';
}

// ============================================================================
// PORTFOLIO VALIDATION
// ============================================================================

/**
 * Validate portfolio against tier-specific requirements
 * Returns array of warnings and successes
 */
export function validatePortfolio(colleges, studentProfile) {
  const warnings = [];
  const successes = [];

  if (!colleges || colleges.length === 0) {
    return { warnings: ['No colleges in portfolio'], successes: [] };
  }

  const studentTier = getStudentTier(studentProfile);
  const definitions = getTierDefinitions(studentTier);

  // Group colleges by tier
  const reaches = colleges.filter(c => c.category?.toLowerCase() === 'reach');
  const targets = colleges.filter(c => c.category?.toLowerCase() === 'target');
  const safeties = colleges.filter(c => c.category?.toLowerCase() === 'safety');

  // VALIDATION 1: Minimum diversity requirements for targets
  if (definitions.target.minimum_above_25) {
    const targetsWith25Plus = targets.filter(c =>
      (c.global_colleges?.acceptance_rate || 0) >= 0.25
    );
    if (targetsWith25Plus.length < definitions.target.minimum_above_25) {
      warnings.push({
        type: 'diversity',
        message: `Add ${definitions.target.minimum_above_25 - targetsWith25Plus.length} more target school(s) with ≥25% acceptance for better balance`,
        severity: 'medium'
      });
    } else {
      successes.push('✓ Good diversity in target tier acceptance rates');
    }
  }

  if (definitions.target.minimum_above_30) {
    const targetsWith30Plus = targets.filter(c =>
      (c.global_colleges?.acceptance_rate || 0) >= 0.30
    );
    if (targetsWith30Plus.length < definitions.target.minimum_above_30) {
      warnings.push({
        type: 'diversity',
        message: `Add ${definitions.target.minimum_above_30 - targetsWith30Plus.length} more target school(s) with ≥30% acceptance`,
        severity: 'medium'
      });
    }
  }

  if (definitions.target.minimum_above_35) {
    const targetsWith35Plus = targets.filter(c =>
      (c.global_colleges?.acceptance_rate || 0) >= 0.35
    );
    if (targetsWith35Plus.length < definitions.target.minimum_above_35) {
      warnings.push({
        type: 'diversity',
        message: `Add ${definitions.target.minimum_above_35 - targetsWith35Plus.length} more target school(s) with ≥35% acceptance`,
        severity: 'medium'
      });
    }
  }

  if (definitions.target.minimum_above_45) {
    const targetsWith45Plus = targets.filter(c =>
      (c.global_colleges?.acceptance_rate || 0) >= 0.45
    );
    if (targetsWith45Plus.length < definitions.target.minimum_above_45) {
      warnings.push({
        type: 'diversity',
        message: `Add ${definitions.target.minimum_above_45 - targetsWith45Plus.length} more target school(s) with ≥45% acceptance`,
        severity: 'medium'
      });
    }
  }

  // VALIDATION 2: Safety net check
  const safetiesAbove50 = safeties.filter(c =>
    (c.global_colleges?.acceptance_rate || 0) > 0.50
  );
  if (safeties.length > 0 && safetiesAbove50.length < 1) {
    warnings.push({
      type: 'safety',
      message: 'Include at least 1 safety school with >50% acceptance to ensure admission options',
      severity: 'high'
    });
  } else if (safetiesAbove50.length >= 1) {
    successes.push('✓ Strong safety net with likely admits');
  }

  // VALIDATION 3: Reach-heavy portfolio
  if (reaches.length > 0 && reaches.length > (targets.length + safeties.length)) {
    warnings.push({
      type: 'balance',
      message: 'Portfolio is reach-heavy. Consider adding more target and safety schools to ensure admission offers',
      severity: 'high'
    });
  } else if (reaches.length + targets.length + safeties.length >= 10) {
    successes.push('✓ Well-balanced portfolio across all tiers');
  }

  // VALIDATION 4: Geographic concentration
  const states = [...new Set(colleges.map(c => c.global_colleges?.state).filter(Boolean))];
  if (states.length <= 2 && colleges.length >= 8) {
    warnings.push({
      type: 'geographic',
      message: 'Consider adding geographic diversity to your portfolio',
      severity: 'low'
    });
  } else if (states.length >= 4) {
    successes.push('✓ Good geographic diversity');
  }

  // VALIDATION 5: SAT mismatch warnings (if SAT data available)
  const studentSAT = studentProfile?.sat_total || studentProfile?.sat_score || studentProfile?.test_scores?.sat;
  if (studentSAT) {
    colleges.forEach(c => {
      const satRange = c.global_colleges?.sat_range;
      if (satRange && typeof satRange === 'string') {
        // Parse SAT range like "1400-1550"
        const match = satRange.match(/(\d+)-(\d+)/);
        if (match) {
          const [_, lower, upper] = match;
          const sat25th = parseInt(lower);

          if (studentSAT < sat25th - 50) {
            warnings.push({
              type: 'sat_mismatch',
              message: `${c.global_colleges?.name || c.name}: Your SAT (${studentSAT}) is below typical range (${satRange})`,
              severity: 'medium',
              collegeName: c.global_colleges?.name || c.name
            });
          }
        }
      }
    });
  }

  return { warnings, successes };
}

/**
 * Format acceptance rate for display
 */
export function formatAcceptance(rate) {
  if (!rate && rate !== 0) return 'N/A';
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * Determine if a college is appropriately categorized for student's tier
 */
export function validateCollegeTier(college, assignedTier, studentTier) {
  const definitions = getTierDefinitions(studentTier);
  const acceptanceRate = college.global_colleges?.acceptance_rate || college.acceptance_rate;

  if (!acceptanceRate) return { valid: null, reason: 'Acceptance rate not available' };

  const tierDef = definitions[assignedTier];
  if (!tierDef) return { valid: null, reason: 'Invalid tier' };

  // Check if acceptance rate falls within tier definition
  if (tierDef.acceptance_max && acceptanceRate > tierDef.acceptance_max) {
    return {
      valid: false,
      reason: `Acceptance rate (${formatAcceptance(acceptanceRate)}) is above ${assignedTier} threshold (${formatAcceptance(tierDef.acceptance_max)})`
    };
  }

  if (tierDef.acceptance_min && acceptanceRate < tierDef.acceptance_min) {
    return {
      valid: false,
      reason: `Acceptance rate (${formatAcceptance(acceptanceRate)}) is below ${assignedTier} threshold (${formatAcceptance(tierDef.acceptance_min)})`
    };
  }

  return { valid: true, reason: 'Appropriately categorized' };
}

export default {
  STUDENT_TIERS,
  getStudentTier,
  getTierDefinitions,
  getTierLabel,
  validatePortfolio,
  formatAcceptance,
  validateCollegeTier
};
